import React from 'react';
import {
  IconButton,
  Divider,
  TextField,
  Typography,
  Menu,
  Button,
  Link,
  MenuItem,
  Badge,
  alpha,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import { Toolbar } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import { EvacZonesFilter, FiltersType } from '../../types';
import { FilterMenu } from './FilterMenu';
import { CopyMenu } from './CopyMenu';
import { notifyOfChanges } from './utils';

export type EvacuationZonesToolbarProps = {
  evacZoneStyles: RegionEvacZoneStyle[];
  selected: number;
  searchQueryState: [string, React.Dispatch<React.SetStateAction<string>>];
  handleCopyZones: (filter: EvacZonesFilter) => void;
  handleSetSelectedZoneStatus: (
    status?: EvacZoneStatus,
    acceptExternalStatus?: boolean,
  ) => number;
  enabledEvacZoneStatuses: EvacZoneStatus[];
  filters: FiltersType;
  onCheckboxChange: (key: keyof FiltersType, subKey?: string) => void;
  isFiltered: boolean;
};
export const EvacuationZonesToolbar = ({
  evacZoneStyles,
  selected,
  searchQueryState,
  handleCopyZones,
  handleSetSelectedZoneStatus,
  enabledEvacZoneStatuses,
  filters,
  onCheckboxChange,
  isFiltered,
}: EvacuationZonesToolbarProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = searchQueryState;
  const [isSearchOpen, setIsSearchOpen] = React.useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [copyMenuAnchorEl, setCopyMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const handleCopyMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setCopyMenuAnchorEl(event.currentTarget);
  };

  const handleCopyMenuClose = (): void => {
    setCopyMenuAnchorEl(null);
  };

  if (selected > 0) {
    return (
      <Toolbar
        style={{ backgroundColor: alpha(theme.palette.info.light, 0.3) }}
      >
        <Typography fontWeight="medium" sx={{ flex: 1, margin: '0 0.5rem' }}>
          {t('geoEventEvacuations.evacZones.selectionBar.selected', {
            count: selected,
          })}
        </Typography>
        <Button
          component={Link}
          variant="text"
          color="info"
          onClick={() => {
            const updated = handleSetSelectedZoneStatus(undefined, true);
            notifyOfChanges(t, updated);
          }}
        >
          {t('geoEventEvacuations.evacZones.selectionBar.acceptChanges')}
        </Button>
        <Button color="info" onClick={handleMenuOpen} sx={{ margin: '0 1rem' }}>
          <Typography variant="button">
            {t('geoEventEvacuations.evacZones.selectionBar.editStatus')}
          </Typography>
        </Button>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            key="normal"
            onClick={() => {
              const updated = handleSetSelectedZoneStatus(undefined);
              handleMenuClose();
              notifyOfChanges(t, updated);
            }}
          >
            {t(`geoEventEvacuations.evacZones.normal`)}
          </MenuItem>
          {/* loop through evac zone status */}
          {Object.values(enabledEvacZoneStatuses).map((status) => (
            <MenuItem
              key={status}
              onClick={() => {
                const updated = handleSetSelectedZoneStatus(status);
                handleMenuClose();
                notifyOfChanges(t, updated);
              }}
            >
              {t(`geoEventEvacuations.evacZones.${status}`)}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    );
  }

  return (
    <Toolbar>
      <Typography
        component="h3"
        fontWeight="bold"
        sx={{ flex: 1, marginLeft: 1 }}
      >
        {t('geoEventEvacuations.evacZones.table.title')}
      </Typography>
      <IconButton
        onClick={handleMenuOpen}
        aria-label={t('geoEventEvacuations.evacZones.table.filter.label')}
        size="small"
      >
        <Badge color="error" variant="dot" invisible={!isFiltered}>
          <FilterListIcon />
        </Badge>
      </IconButton>
      <FilterMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        evacZoneStyles={evacZoneStyles}
        filters={filters}
        onCheckboxChange={onCheckboxChange}
      />
      <IconButton
        onClick={handleCopyMenuOpen}
        aria-label={t('geoEventEvacuations.evacZones.table.copy')}
      >
        <ContentCopyIcon />
      </IconButton>
      <CopyMenu
        anchorEl={copyMenuAnchorEl}
        open={Boolean(copyMenuAnchorEl)}
        onClose={handleCopyMenuClose}
        evacZoneStyles={evacZoneStyles}
        handleCopyZones={handleCopyZones}
      />
      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
      {isSearchOpen ? (
        <TextField
          autoFocus
          size="small"
          placeholder={t('geoEventEvacuations.evacZones.table.search')}
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onBlur={() => !searchQuery && setIsSearchOpen(false)}
          sx={{ width: 200 }}
          variant="outlined"
        />
      ) : (
        <IconButton
          size="small"
          onClick={() => setIsSearchOpen(true)}
          aria-label={t('geoEventEvacuations.evacZones.table.search')}
        >
          <SearchIcon />
        </IconButton>
      )}
    </Toolbar>
  );
};
